import React, { FC, useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Navbar, NavbarToggler } from 'reactstrap';
import styled from 'styled-components';
import { useApolloClient } from '@apollo/client';
import { Trans, useTranslation } from '@blocs.i18n';
import { ProfileQueries } from '@minecraft.shared-queries';
import { grid } from '@minecraft.themes';
import appConfig from 'config/appConfig';
import { saveStorageKeys } from 'lib/localAuthStore';
import SCREEN_NAMES from 'config/constants/screenNames';
import routeHelper from 'utils/routeHelper';
import NavLoggedIn from 'components/core/NavLoggedIn';
import {
  PaywallLocation,
  TrialDaysCountdown,
  useRegistrationLocation,
  ResumeSubscriptionBanner,
  useBillingPopup,
} from '@blocs.paywall';
import { useUserContext } from '@blocs.auth';
import { BodyText, BodyTextLarge, FlexRow, LinkAsButton } from '@minecraft.atoms';
import Logo from './Logo';
import { useArtistContext } from './ArtistContext';

type Props = {
  hideNavigation: boolean;
  logout: () => void;
  showLogoutWhenHiddenNav?: boolean;
  location?: PaywallLocation;
};

const closeIcon = `${appConfig.svgRoot}close.svg`;

const NavStyles = styled.div`
  margin: 0;
  padding: 0;
  width: 100%;
  nav {
    padding: 0.6875rem 1.875rem 0.5rem 1.75rem;
    z-index: 2;
    @media screen and (max-width: 768px) {
      padding-left: 0.875rem;
      padding-right: 0.9375rem;
    }
    @media screen and (max-width: ${grid.breakpoints.xxlg}) {
      flex-wrap: wrap;
    }
  }

  .nav-link {
    padding-top: 0.875rem;
  }

  .navbar,
  hr {
    display: flex;
    width: 100%;
  }
  li {
    margin-top: auto;
  }
  a,
  button {
    position: relative;
    text-transform: capitalize;
    cursor: pointer;
    color: ${(props): string => props.theme.colors.primary.black};
  }
  button,
  a {
    color: ${(props): string => props.theme.colors.primary.primary};
    outline: none;
  }
  .active {
    color: ${(props): string => props.theme.colors.primary.black};
    font-weight: bold;
  }
  a:active,
  button:active,
  button.dropdown-item:active {
    background-color: inherit;
  }
`;

const StyledNavToggle = styled(NavbarToggler)<{ $isOpen: boolean }>`
  span.navbar-toggler-icon {
    ${(props): string => (props.$isOpen ? `background-image: url(${closeIcon});` : '')}
  }
`;

const StyledLink = styled(Link)`
  margin-right: 1.5625rem;
`;

const StyledNavbar = styled(Navbar)`
  justify-content: space-between;
`;

const Nav: FC<Props> = ({ logout, hideNavigation, showLogoutWhenHiddenNav, location }) => {
  const { subscription, artistId } = useUserContext();
  const { isExpiredCardlessTrial } = useArtistContext();
  const billingPopup = useBillingPopup();
  const { isRegistration } = useRegistrationLocation({ paywallLocation: PaywallLocation.SIGNUP });
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const client = useApolloClient();
  const {
    location: { pathname },
  } = useHistory();

  // close menu on page change
  useEffect(() => {
    if (isOpen) setIsOpen(false);
  }, [pathname]);

  const toggle = (): void => setIsOpen(!isOpen);

  const handleLogout = (): void => {
    const { cache } = client;

    saveStorageKeys(() => {
      cache.writeQuery({
        query: ProfileQueries.IS_LOGGED_IN,
        data: {
          token: null,
          isLoggedIn: false,
        },
      });
      logout();
    });
  };

  return (
    <>
      <ResumeSubscriptionBanner onClick={() => billingPopup.show({})} />
      <TrialDaysCountdown subscription={subscription} artistId={artistId} />
      <NavStyles>
        <StyledNavbar light expand="md" data-testid="talent-main-nav-wrapper">
          {hideNavigation ? (
            <>
              <StyledLink
                data-testid={`${SCREEN_NAMES.NAVIGATION_LINK}-casting-networks-logo`}
                data-qa-id={`${SCREEN_NAMES.NAVIGATION_LINK}-casting-networks-logo`}
                aria-label="Casting Networks"
                {...routeHelper.defaultRedirect.forLink()}
              >
                <Logo />
              </StyledLink>
              {!isRegistration && showLogoutWhenHiddenNav && (
                <BodyTextLarge as="span" className="cn_atom_px-2">
                  <LinkAsButton
                    onClick={handleLogout}
                    label={
                      location === PaywallLocation.SIGNUP
                        ? t('common:header.cancelRegistration')
                        : t('common:header.logout')
                    }
                  />
                </BodyTextLarge>
              )}
            </>
          ) : (
            <>
              <StyledLink
                data-qa-id={`${SCREEN_NAMES.NAVIGATION_LINK}-casting-networks-logo`}
                data-testid={`${SCREEN_NAMES.NAVIGATION_LINK}-casting-networks-logo`}
                aria-label="Casting Networks"
                {...routeHelper.defaultRedirect.forLink()}
              >
                <Logo />
              </StyledLink>
              <StyledNavToggle $isOpen={isOpen} onClick={toggle} />
              <NavLoggedIn isOpen={isOpen} handleLogout={handleLogout} />
            </>
          )}
        </StyledNavbar>
      </NavStyles>
      {isExpiredCardlessTrial && (
        <FlexRow
          bgColor="danger"
          alignItems="center"
          justifyContent="center"
          className="cn_atom_p-4"
          gap="1"
          data-testid="expired-trial-banner"
        >
          <Trans
            i18nKey="common:freeTrial.banner.expired"
            components={{
              lnk: (
                <LinkAsButton
                  onClick={() => billingPopup.show({ location: PaywallLocation.EXPIRED_CARDLESS_TRIAL_MODAL })}
                />
              ),
              text: <BodyText color="error" fontWeight="medium" />,
            }}
          />
        </FlexRow>
      )}
    </>
  );
};

export default Nav;
